import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

function TournamentSchedule() {
  const { t } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [teams, setTeams] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSchedule();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchSchedule = async () => {
    try {
      const [tournamentRes, teamsRes, matchesRes, scheduleRes] = await Promise.all([
        axios.get(`/api/tournaments/${id}`),
        axios.get(`/api/tournaments/${id}/teams`),
        axios.get(`/api/tournaments/${id}/matches`),
        axios.get(`/api/tournaments/${id}/schedule`)
      ]);

      setTournament(tournamentRes.data);
      setTeams(teamsRes.data);
      setMatches(matchesRes.data);

      if (scheduleRes.data.schedule) {
        setSchedule(scheduleRes.data.schedule);
      } else {
        setError(t('schedule.notAvailableError'));
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading schedule:', err);
      setError(t('schedule.loadError'));
      setLoading(false);
    }
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    if (!team) return 'TBD';

    if (tournament?.tournament_type === 'groups') {
      return team.players?.map(p => p.name).join(' / ') || team.name;
    }
    return team.name;
  };

  const formatTime = (timeString) => {
    return timeString || '--:--';
  };

  const getMatchResult = (matchId) => {
    const match = matches.find(m => m.id === matchId);
    if (!match || match.status !== 'beendet') return null;

    if (match.team1_set1 !== null) {
      const sets = [];
      sets.push(`${match.team1_set1}:${match.team2_set1}`);
      if (match.team1_set2 !== null) {
        sets.push(`${match.team1_set2}:${match.team2_set2}`);
      }
      if (match.team1_set3 !== null && (match.team1_set3 > 0 || match.team2_set3 > 0)) {
        sets.push(`${match.team1_set3}:${match.team2_set3}`);
      }
      return { sets, isBestOfThree: true };
    }

    if (match.team1_score !== null) {
      return { score: `${match.team1_score}:${match.team2_score}`, isBestOfThree: false };
    }

    return null;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-4 sm:p-8 min-h-screen bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-800">
        <div className="text-center p-12 text-lg text-slate-500">{t('schedule.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-4 sm:p-8 min-h-screen bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-800">
        <div className="bg-white rounded-xl p-4 sm:p-8 mb-8 shadow-md dark:bg-slate-800">
          <button
            onClick={() => navigate(-1)}
            className="bg-transparent border-2 border-gold text-gold px-6 py-2 rounded-lg text-base font-semibold cursor-pointer transition-all mb-4 hover:bg-gold hover:text-white"
          >
            ← {t('common.back')}
          </button>
        </div>
        <div className="bg-white rounded-xl p-12 shadow-md text-center dark:bg-slate-800">
          <div className="text-5xl mb-4 text-gold">⏰</div>
          <h2 className="text-slate-900 mb-4 text-xl font-bold dark:text-white">
            {t('schedule.notAvailable')}
          </h2>
          <p className="text-slate-500 text-lg mb-8">
            {t('schedule.notAvailableMsg')}
          </p>
          <p className="text-slate-500 text-base">
            {t('schedule.notAvailableHint')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-8 min-h-screen bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-800">
      {/* Header */}
      <div className="bg-white rounded-xl p-4 sm:p-8 mb-8 shadow-md dark:bg-slate-800">
        <button
          onClick={() => navigate(-1)}
          className="bg-transparent border-2 border-gold text-gold px-6 py-2 rounded-lg text-base font-semibold cursor-pointer transition-all mb-4 hover:bg-gold hover:text-white"
        >
          ← {t('common.back')}
        </button>
        <div>
          <h1 className="my-2 text-slate-900 text-2xl sm:text-3xl font-bold dark:text-white">{tournament.name}</h1>
          <h2 className="my-2 mb-6 text-slate-500 text-lg sm:text-xl font-medium">{t('schedule.title')}</h2>
          {tournament.start_time && (
            <div className="flex gap-8 flex-wrap p-4 bg-gold/10 rounded-lg border-l-4 border-gold">
              <div className="flex flex-col gap-1">
                <span className="text-sm text-slate-500 font-medium">{t('schedule.start')}:</span>
                <span className="text-lg text-slate-900 font-semibold dark:text-white">{tournament.start_time}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm text-slate-500 font-medium">{t('schedule.duration')}:</span>
                <span className="text-lg text-slate-900 font-semibold dark:text-white">{tournament.match_duration} {t('schedule.min')}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm text-slate-500 font-medium">{t('schedule.break')}:</span>
                <span className="text-lg text-slate-900 font-semibold dark:text-white">{tournament.break_duration} {t('schedule.min')}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Schedule Table */}
      <div className="bg-white rounded-xl p-4 sm:p-8 shadow-md dark:bg-slate-800">
        <div className="flex flex-col gap-2">
          {/* Table Header - hidden on mobile */}
          <div className="hidden md:grid grid-cols-[120px_150px_120px_1fr_140px] gap-4 p-4 bg-slate-900 text-white rounded-lg font-semibold text-sm uppercase tracking-wide">
            <div>{t('schedule.time')}</div>
            <div>{t('schedule.match')}</div>
            <div>{t('schedule.court')}</div>
            <div>{t('schedule.teams')}</div>
            <div>{t('schedule.status')}</div>
          </div>

          {schedule.map((match) => {
            const result = getMatchResult(match.match_id);
            const isCompleted = match.status === 'beendet';
            const isOngoing = match.status === 'läuft';

            return (
              <div
                key={match.match_id}
                className={`grid grid-cols-1 md:grid-cols-[120px_150px_120px_1fr_140px] gap-4 p-4 bg-white border rounded-lg items-center transition-all hover:bg-gold/5 hover:border-gold/40 hover:translate-x-1 hover:shadow-md dark:bg-slate-800 dark:border-slate-700
                  ${isCompleted ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' : ''}
                  ${isOngoing ? 'bg-gold/10 border-gold border-2' : 'border-gold/20'}
                `}
              >
                {/* Time */}
                <div className="flex md:flex-col gap-1 justify-between md:justify-start">
                  <span className="md:hidden text-sm font-semibold text-slate-500 uppercase">{t('schedule.time')}</span>
                  <div>
                    <div className="text-lg font-semibold text-slate-900 dark:text-white">{formatTime(match.start_time)}</div>
                    <div className="text-sm text-slate-500">-{formatTime(match.end_time)}</div>
                  </div>
                </div>

                {/* Match Info */}
                <div className="flex md:flex-col gap-1 justify-between md:justify-start">
                  <span className="md:hidden text-sm font-semibold text-slate-500 uppercase">{t('schedule.match')}</span>
                  <div>
                    <div className="text-base font-semibold text-gold">#{match.match_number}</div>
                    <div className="text-sm text-slate-500">{match.round}</div>
                  </div>
                </div>

                {/* Court */}
                <div className="flex justify-between md:justify-center">
                  <span className="md:hidden text-sm font-semibold text-slate-500 uppercase">{t('schedule.court')}</span>
                  <span className="text-base font-semibold text-slate-900 text-center px-2 py-1 bg-gold/10 rounded-md dark:text-white">
                    {match.court || 'TBD'}
                  </span>
                </div>

                {/* Teams */}
                <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4 min-w-0">
                  <span className="md:hidden text-sm font-semibold text-slate-500 uppercase">{t('schedule.teams')}</span>
                  <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4 w-full">
                    <span className="flex-1 font-medium text-slate-900 break-words dark:text-white">{getTeamName(match.team1_id)}</span>
                    <span className="font-bold text-slate-500 text-sm shrink-0">
                      {result ? (
                        result.isBestOfThree ? (
                          <div className="flex flex-col gap-0.5 text-sm">
                            {result.sets.map((set, idx) => (
                              <div key={idx}>{set}</div>
                            ))}
                          </div>
                        ) : (
                          result.score
                        )
                      ) : (
                        'vs'
                      )}
                    </span>
                    <span className="flex-1 font-medium text-slate-900 break-words dark:text-white">{getTeamName(match.team2_id)}</span>
                  </div>
                </div>

                {/* Status */}
                <div className="flex justify-between md:justify-center">
                  <span className="md:hidden text-sm font-semibold text-slate-500 uppercase">{t('schedule.status')}</span>
                  <span className={`inline-block px-4 py-1.5 rounded-full text-sm font-semibold
                    ${isCompleted ? 'bg-green-100 text-green-600' : ''}
                    ${isOngoing ? 'bg-gold/20 text-amber-600' : ''}
                    ${!isCompleted && !isOngoing ? 'bg-slate-100 text-slate-500' : ''}
                  `}>
                    {isCompleted ? t('schedule.completed') : isOngoing ? t('schedule.ongoing') : t('schedule.open')}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {schedule.length === 0 && (
          <div className="text-center p-12 text-slate-500 text-lg">
            <p>{t('schedule.noSchedule')}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default TournamentSchedule;
